import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiChevronDown, HiOutlineMenu, HiX } from 'react-icons/hi';
import { MdDashboard, MdOutlineHomeWork } from 'react-icons/md';
import { Link, NavLink, useLocation } from 'react-router-dom';
import axiosInstance from '../axios';
import '../styles/header.css';
import { AUTH_CHANGED_EVENT, getAuthSnapshot, isStoredAuthExpired } from '../utils/auth';

const STATIC_LINKS = [
    { to: '/', label: 'Beranda', end: true },
    { to: '/about', label: 'Tentang Kami' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Kontak' },
];

const MENU_CACHE_KEY = 'mp_menus';
const MENU_CACHE_TIME_KEY = 'mp_menus_updated_at';
const MENU_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 menit
const MENU_CACHE_TTL = 30 * 60 * 1000; // 30 menit

const readStorage = (key) => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(key);
};

const getStoredUser = () => {
    try {
        const raw = readStorage('mp_user');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const getStoredToken = () => readStorage('mp_token');

const isAuthExpiredSafe = () => {
    if (typeof window === 'undefined') return true;
    return isStoredAuthExpired();
};

const getCachedMenus = () => {
    try {
        const raw = readStorage(MENU_CACHE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const setCachedMenus = (menus) => {
    try {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(menus || []));
        window.localStorage.setItem(MENU_CACHE_TIME_KEY, String(Date.now()));
    } catch {
        // cache write failure should not break UI
    }
};

const isMenuCacheFresh = () => {
    try {
        const raw = readStorage(MENU_CACHE_TIME_KEY);
        if (!raw) return false;

        const updatedAt = Number(raw);
        if (!Number.isFinite(updatedAt) || updatedAt <= 0) return false;

        return Date.now() - updatedAt < MENU_CACHE_TTL;
    } catch {
        return false;
    }
};

const buildMenuHref = (item) => {
    if (!item) return '/';

    if (item.type === 'external') {
        return item.path || item.url || '#';
    }

    if (item.type === 'filter') {
        const params = new URLSearchParams(item.params || {});
        const queryString = params.toString();
        return `${item.path || '/property'}${queryString ? `?${queryString}` : ''}`;
    }

    return item.path || item.url || '/';
};

const Header = () => {
    const location = useLocation();
    const headerRef = useRef(null);
    const menusRef = useRef([]);
    const refreshTimerRef = useRef(null);

    const [menus, setMenus] = useState(() => getCachedMenus());
    const [isNavbarOpen, setIsNavbarOpen] = useState(false);
    const [activeDesktopMenu, setActiveDesktopMenu] = useState(null);
    const [activeMobileMenu, setActiveMobileMenu] = useState(null);

    const isFetchingMenusRef = useRef(false);

    const isExternalMenu = (item) => item?.type === 'external';

    const isMountedRef = useRef(true);
    const [authSnapshot, setAuthSnapshot] = useState(() => getAuthSnapshot());

    const isLoggedIn = authSnapshot.authenticated;

    const closeNavbar = useCallback(() => {
        setIsNavbarOpen(false);
        setActiveDesktopMenu(null);
        setActiveMobileMenu(null);
    }, []);

    const toggleNavbar = useCallback(() => {
        setIsNavbarOpen((prev) => !prev);
    }, []);

    const primaryMenus = useMemo(() => {
        if (!Array.isArray(menus)) return [];
        return menus.filter((menu) => Array.isArray(menu?.groups) && menu.groups.length > 0);
    }, [menus]);

    const fetchMenus = useCallback(async () => {
        if (isFetchingMenusRef.current) {
            return;
        }

        isFetchingMenusRef.current = true;
        try {
            const response = await axiosInstance.get('/menus');
            const data = Array.isArray(response.data?.data) ? response.data.data : [];

            if (data.length > 0) {
                setMenus(data);
                menusRef.current = data;
                setCachedMenus(data);
            } else if (menusRef.current.length === 0) {
                setMenus([]);
            }
        } catch (error) {
            console.error('Failed to load menus:', error);
        } finally {
            isFetchingMenusRef.current = false;
        }
    }, []);

    useEffect(() => {
        menusRef.current = menus;
    }, [menus]);

    useEffect(() => {
        let mounted = true;

        const runBackgroundSync = async () => {
            if (!mounted) return;

            const shouldRefreshImmediately = menusRef.current.length === 0 || !isMenuCacheFresh();

            if (shouldRefreshImmediately) {
                await fetchMenus();
            }
        };

        runBackgroundSync();

        if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current);
        }

        refreshTimerRef.current = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchMenus();
            }
        }, MENU_REFRESH_INTERVAL);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchMenus();
            }
        };

        const handleWindowFocus = () => {
            fetchMenus();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleWindowFocus);

        return () => {
            mounted = false;

            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }

            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [fetchMenus]);

    useEffect(() => {
        closeNavbar();
    }, [location.pathname, location.search, closeNavbar]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const syncAuth = () => {
            setAuthSnapshot(getAuthSnapshot());
        };

        syncAuth();

        window.addEventListener('storage', (event) => {
            if (event.key?.startsWith('mp_') || event.key === null) {
                syncAuth();
            }
        });
        window.addEventListener(AUTH_CHANGED_EVENT, syncAuth);

        return () => {
            window.removeEventListener('storage', syncAuth);
            window.removeEventListener(AUTH_CHANGED_EVENT, syncAuth);
        };
    }, []);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                closeNavbar();
            }
        };

        const handleOutsideClick = (event) => {
            if (headerRef.current && !headerRef.current.contains(event.target)) {
                closeNavbar();
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick);

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [closeNavbar]);

    useEffect(() => {
        if (isNavbarOpen) {
            const scrollY = window.scrollY;
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';

            return () => {
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isNavbarOpen]);

    const renderDesktopMenu = (menu) => {
        const isOpen = activeDesktopMenu === menu.slug;

        return (
            <div key={menu.id} className={`site-menu ${isOpen ? 'is-open' : ''}`}>
                <button
                    type="button"
                    className="site-menu__toggle"
                    aria-expanded={isOpen}
                    aria-label={`Buka menu ${menu.name}`}
                    onClick={() => setActiveDesktopMenu((prev) => (prev === menu.slug ? null : menu.slug))}
                >
                    <span>{menu.name}</span>
                    <HiChevronDown />
                </button>

                <div className="site-menu__panel" role="menu" aria-label={menu.name}>
                    <div className="site-menu__panel-inner">
                        <div className="site-menu__panel-header">
                            <div className="site-menu__panel-title">{menu.name}</div>
                            <div className="site-menu__panel-subtitle">Pilih kategori properti yang Anda butuhkan</div>
                        </div>

                        <div className="site-menu__grid">
                            {(menu.groups || []).map((group) => (
                                <div key={group.id} className="site-menu__group">
                                    <div className="site-menu__group-title">{group.name}</div>

                                    <div className="site-menu__items">
                                        {(group.items || []).map((item) => {
                                            const href = buildMenuHref(item);

                                            return isExternalMenu(item) ? (
                                                <a
                                                    key={item.id}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="site-menu__item"
                                                    onClick={closeNavbar}
                                                >
                                                    <span className="site-menu__item-name">{item.name}</span>

                                                    {item.badge && (
                                                        <span
                                                            className={`site-menu__badge site-menu__badge--${
                                                                item.badge_color || 'danger'
                                                            }`}
                                                        >
                                                            {item.badge}
                                                        </span>
                                                    )}
                                                </a>
                                            ) : (
                                                <Link
                                                    key={item.id}
                                                    to={href}
                                                    className="site-menu__item"
                                                    onClick={closeNavbar}
                                                >
                                                    <span className="site-menu__item-name">{item.name}</span>

                                                    {item.badge && (
                                                        <span
                                                            className={`site-menu__badge site-menu__badge--${
                                                                item.badge_color || 'danger'
                                                            }`}
                                                        >
                                                            {item.badge}
                                                        </span>
                                                    )}
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderMobileMenu = (menu) => {
        const isOpen = activeMobileMenu === menu.slug;

        return (
            <div key={menu.id} className={`site-mobile-section ${isOpen ? 'is-open' : ''}`}>
                <button
                    type="button"
                    className="site-mobile-section__toggle"
                    aria-expanded={isOpen}
                    aria-label={`Buka menu ${menu.name}`}
                    onClick={() => setActiveMobileMenu((prev) => (prev === menu.slug ? null : menu.slug))}
                >
                    <span>{menu.name}</span>
                    <HiChevronDown />
                </button>

                <div className="site-mobile-section__panel">
                    <div className="site-mobile-section__inner">
                        {(menu.groups || []).map((group) => (
                            <div key={group.id} className="site-mobile-group">
                                <div className="site-mobile-group__title">{group.name}</div>

                                <div className="site-mobile-group__items">
                                    {(group.items || []).map((item) => {
                                        const href = buildMenuHref(item);

                                        return isExternalMenu(item) ? (
                                            <a
                                                key={item.id}
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="site-mobile-group__item"
                                                onClick={closeNavbar}
                                            >
                                                <span className="site-mobile-group__item-name">{item.name}</span>

                                                {item.badge && (
                                                    <span
                                                        className={`site-mobile-group__badge site-mobile-group__badge--${
                                                            item.badge_color || 'danger'
                                                        }`}
                                                    >
                                                        {item.badge}
                                                    </span>
                                                )}
                                            </a>
                                        ) : (
                                            <Link
                                                key={item.id}
                                                to={href}
                                                className="site-mobile-group__item"
                                                onClick={closeNavbar}
                                            >
                                                <span className="site-mobile-group__item-name">{item.name}</span>

                                                {item.badge && (
                                                    <span
                                                        className={`site-mobile-group__badge site-mobile-group__badge--${
                                                            item.badge_color || 'danger'
                                                        }`}
                                                    >
                                                        {item.badge}
                                                    </span>
                                                )}
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <header className="site-header" ref={headerRef}>
            <nav className="site-navbar">
                <div className="site-navbar__inner">
                    <div className="site-navbar__left">
                        <Link className="site-navbar__brand" to="/" onClick={closeNavbar}>
                            <img src="/logo.png" alt="Urbania" className="site-navbar__logo" />
                        </Link>
                    </div>

                    <div className="site-navbar__right">
                        <button
                            className="site-navbar__toggle"
                            type="button"
                            onClick={toggleNavbar}
                            aria-controls="site-navbar-menu"
                            aria-expanded={isNavbarOpen}
                            aria-label="Toggle navigation"
                        >
                            {isNavbarOpen ? <HiX size={24} /> : <HiOutlineMenu size={24} />}
                        </button>

                        <div id="site-navbar-menu" className={`site-navbar__menu ${isNavbarOpen ? 'is-open' : ''}`}>
                            <div className="site-navbar__desktop-row">
                                <div className="site-navbar__links site-navbar__links--desktop">
                                    {STATIC_LINKS.map(({ to, label, end }) => (
                                        <NavLink
                                            key={to}
                                            className={({ isActive }) => `site-nav-link ${isActive ? 'active' : ''}`}
                                            to={to}
                                            end={end}
                                            onClick={closeNavbar}
                                        >
                                            {label}
                                        </NavLink>
                                    ))}

                                    {primaryMenus.map(renderDesktopMenu)}
                                </div>

                                <div className="site-navbar__actions">
                                    {isLoggedIn ? (
                                        <NavLink
                                            to="/dashboard"
                                            className={({ isActive }) =>
                                                `site-nav-btn site-nav-btn--outline ${isActive ? 'active' : ''}`
                                            }
                                            onClick={closeNavbar}
                                        >
                                            <MdDashboard size={16} />
                                            Dashboard
                                        </NavLink>
                                    ) : null}

                                    <NavLink
                                        to="/sell-property"
                                        className="site-nav-btn site-nav-btn--primary"
                                        onClick={closeNavbar}
                                    >
                                        <MdOutlineHomeWork size={16} />
                                        Jual / Sewakan
                                    </NavLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <div
                id="site-navbar-mobile"
                className={`site-navbar__mobile-menu ${isNavbarOpen ? 'is-open' : ''}`}
                aria-hidden={!isNavbarOpen}
            >
                <div className="site-navbar__mobile-links">
                    {STATIC_LINKS.map(({ to, label, end }) => (
                        <NavLink
                            key={to}
                            className={({ isActive }) => `site-mobile-link ${isActive ? 'active' : ''}`}
                            to={to}
                            end={end}
                            onClick={closeNavbar}
                        >
                            {label}
                        </NavLink>
                    ))}
                </div>

                {primaryMenus.map(renderMobileMenu)}

                {isLoggedIn ? (
                    <NavLink
                        to="/dashboard"
                        className="site-mobile-cta site-mobile-cta--outline"
                        onClick={closeNavbar}
                    >
                        <MdDashboard size={16} />
                        Dashboard
                    </NavLink>
                ) : null}

                <NavLink
                    to="/sell-property"
                    className="site-mobile-cta site-mobile-cta--primary"
                    onClick={closeNavbar}
                >
                    <MdOutlineHomeWork size={16} />
                    Jual / Sewakan
                </NavLink>
            </div>

            <button
                type="button"
                className={`site-navbar__overlay ${isNavbarOpen ? 'is-open' : ''}`}
                aria-label="Tutup navigasi"
                onClick={closeNavbar}
            />
        </header>
    );
};

export default Header;
